import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Icon from '../icon/Icon'

export default function Select({
  className,
  name,
  placeholder,
  options,
  selectedOption,
  maxHeight,
  full,
  required,
  disabled,
  onChange
}) {
  const listRef = useRef(null)
  const selectRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [currentOption, setCurrentOption] = useState(selectedOption)

  const changeOption = useCallback(
    option => {
      selectRef.current.value = option.value
      selectRef.current.blur()

      const changeEvent = document.createEvent('Event')
      changeEvent.initEvent('change', true, false)
      selectRef.current.dispatchEvent(changeEvent)
    },
    [selectRef]
  )

  const onChangeHandler = e => {
    const option = options.find(o => o.value === e.target.value)
    if (!option) return
    setCurrentOption(option)
    onChange && onChange(option)
  }

  const onMouseDownHandler = e => {
    if (disabled) return
    if (!open && e.target !== selectRef.current) {
      selectRef.current.focus()
      e.preventDefault()
    }
  }

  const onKeyDownHandler = e => {
    if (disabled) return
    if (open && (e.keyCode === 13 || e.keyCode === 32)) {
      selectRef.current.blur()
      e.preventDefault()
    }
  }

  useEffect(() => {
    if (selectedOption) changeOption(selectedOption)
  }, [selectedOption, changeOption])

  useEffect(() => {
    if (!selectRef.current) return

    const changeDetectTimer = setInterval(() => {
      const val = selectRef.current.value || ''
      const selVal = (currentOption && currentOption.value) || ''
      if (val === selVal) return

      const evt = document.createEvent('HTMLEvents')
      evt.initEvent('change', true, false)
      selectRef.current.dispatchEvent(evt)
    }, 100)

    return () => clearInterval(changeDetectTimer)
  }, [currentOption]) // Removed selectRef.current as it's not a valid dependency

  return (
    <div
      className={clsx(
        className,
        'select-container',
        full && 'select-container--full',
        open && 'select-container--ul-expanded'
      )}
      onMouseDown={onMouseDownHandler}
      onKeyDown={onKeyDownHandler}
    >
      <select
        ref={selectRef}
        name={name}
        className="form-control"
        required={required}
        disabled={disabled}
        defaultValue=""
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onChange={onChangeHandler}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map(o => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      <div className="select-container__selectbox" style={{ overflow: 'visible', zIndex: 'auto' }} tabIndex="0">
        <span>{(currentOption && currentOption.label) || placeholder}</span>
        <Icon name="arrow--down" className="icon--medium" />

        {open && (
          <ul
            ref={listRef}
            className={clsx('select-container__ul', 'select-container--ul-expanded')}
            style={{
              width: 'calc(100% + 2px)',
              marginLeft: '-1px',
              overflowX: 'hidden',
              overflowY: 'auto',
              maxHeight
            }}
          >
            {options.map(o => (
              <li key={o.value} onMouseDown={() => changeOption(o)}>
                {o.label}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Icon name="arrow--down" className="icon--medium" />
    </div>
  )
}

Select.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  selectedOption: PropTypes.object,
  maxHeight: PropTypes.string,
  full: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  onChange: PropTypes.func
}

Select.defaultProps = {
  full: false,
  disabled: false,
  required: false,
  options: []
}
